/**
 * Face Shape Prompt Suggestions for Haircuts Studio
 * 
 * Researched, gender-specific haircut suggestions based on face shape.
 * Each face shape has 2 curated prompts per gender.
 * 
 * Usage: Used in Haircuts Studio after Face Analysis to suggest prompts
 * that the user can click to fill the Custom Request textarea.
 */

export const FACE_SHAPE_SUGGESTIONS = {
  men: {
    oval: [
      {
        title: "Classic Taper with Texture",
        prompt: "a man with an oval face: keep balanced proportions with a clean taper on the sides and soft texture on top; avoid a heavy straight fringe that hides the forehead."
      },
      {
        title: "Side-Part / Textured Crop",
        prompt: "a man with an oval face: choose a classic side-part or textured crop with natural volume; keep the sides neat for a sharp, proportional look."
      }
    ],
    round: [
      {
        title: "Quiff / Pompadour with Volume",
        prompt: "a man with a round face: create height and angles with volume on top (quiff/pompadour style) and shorter sides to visually lengthen the face."
      },
      {
        title: "High Fade + Textured Top",
        prompt: "a man with a round face: keep a high or mid fade with a textured top styled upward/forward; avoid wide, flat styles that make the face look rounder."
      }
    ],
    square: [
      {
        title: "Textured Side-Swept Style",
        prompt: "a man with a square face: soften the jawline with a textured, slightly longer top and a gentle side-swept style; avoid very harsh boxy lines."
      },
      {
        title: "Modern Taper with Movement",
        prompt: "a man with a square face: go for a modern taper with messy texture and movement; keep edges clean but not overly sharp or flat on top."
      }
    ],
    heart: [
      {
        title: "Medium Length with Soft Fringe",
        prompt: "a man with a heart-shaped face: balance a wider forehead with medium length and soft fringe/side-sweep; avoid extreme height on top."
      },
      {
        title: "Textured with Fuller Sides",
        prompt: "a man with a heart-shaped face: keep the sides not too tight and add texture around the temples; aim for a fuller look near the jawline."
      }
    ],
    diamond: [
      {
        title: "Textured Fringe + Medium Length",
        prompt: "a man with a diamond face: add softness and width at the forehead with a textured fringe and medium length; avoid ultra-tight fades that over-emphasize cheekbones."
      },
      {
        title: "Side Part with Balanced Volume",
        prompt: "a man with a diamond face: choose a side part with a bit of length on the sides and controlled volume on top for balanced proportions."
      }
    ],
    oblong: [
      {
        title: "Textured Fringe + Moderate Height",
        prompt: "a man with an oblong face: reduce perceived length with a textured fringe and moderate top height; avoid tall pompadours or extra crown volume."
      },
      {
        title: "Fuller Sides + Forward Styling",
        prompt: "a man with an oblong face: keep the sides a little fuller (not a skin fade) and style the top forward with texture to shorten the face visually."
      }
    ]
  },
  women: {
    oval: [
      {
        title: "Butterfly Layers (Face-Framing)",
        prompt: "a woman with an oval face: choose long, face-framing layers (butterfly-style movement) that keep proportions balanced; avoid overly heavy blunt fringe that shortens the face too much."
      },
      {
        title: "Chic Bob / Lob with Layers",
        prompt: "a woman with an oval face: a chic bob or lob with soft texture and light layers to frame cheekbones; keep it balanced and not too top-heavy."
      }
    ],
    round: [
      {
        title: "Collarbone Lob + Side Part",
        prompt: "a woman with a round face: elongate with a collarbone-length lob and layers starting below the chin; add a side part for a slimming effect."
      },
      {
        title: "Long Layers with Soft Waves",
        prompt: "a woman with a round face: long layers with soft waves and face-framing pieces; avoid blunt chin-length cuts that widen the face."
      }
    ],
    square: [
      {
        title: "Layered Waves + Curtain Bangs",
        prompt: "a woman with a square face: soften the jaw with layered waves and face-framing pieces; add curtain or side-swept bangs to reduce sharp angles."
      },
      {
        title: "Angled Lob with Movement",
        prompt: "a woman with a square face: an angled lob (longer in front) with textured ends and movement; avoid very blunt, boxy one-length cuts."
      }
    ],
    heart: [
      {
        title: "Curtain Bangs + Shoulder Length",
        prompt: "a woman with a heart-shaped face: balance a wider forehead with curtain bangs and a shoulder-length cut that adds fullness around the jawline."
      },
      {
        title: "Textured Lob with Soft Waves",
        prompt: "a woman with a heart-shaped face: a textured lob with soft waves and face-framing layers; avoid excessive height at the crown."
      }
    ],
    diamond: [
      {
        title: "Curtain Bangs + Face-Framing Layers",
        prompt: "a woman with a diamond face: soften prominent cheekbones with curtain bangs and face-framing layers that start around the cheekbones and blend down."
      },
      {
        title: "Soft Bob with Side Part",
        prompt: "a woman with a diamond face: a chin-to-collarbone bob with soft texture and side part; avoid super-tight styles that exaggerate cheek width."
      }
    ],
    oblong: [
      {
        title: "Blunt Bangs + Shoulder Layers",
        prompt: "a woman with an oblong face: visually shorten the face with curtain or blunt bangs and shoulder-length layers; avoid extra height on top."
      },
      {
        title: "Blunt Bob with Soft Waves",
        prompt: "a woman with an oblong face: a blunt bob or lob with soft waves and horizontal volume; avoid very long, pin-straight hair that lengthens the face."
      }
    ]
  },
  other: {
    // Default to men's suggestions for 'other' gender
    oval: [
      {
        title: "Classic Taper with Texture",
        prompt: "a person with an oval face: keep balanced proportions with a clean taper on the sides and soft texture on top; avoid a heavy straight fringe that hides the forehead."
      },
      {
        title: "Side-Part / Textured Crop",
        prompt: "a person with an oval face: choose a classic side-part or textured crop with natural volume; keep the sides neat for a sharp, proportional look."
      }
    ],
    round: [
      {
        title: "Quiff / Pompadour with Volume",
        prompt: "a person with a round face: create height and angles with volume on top (quiff/pompadour style) and shorter sides to visually lengthen the face."
      },
      {
        title: "High Fade + Textured Top",
        prompt: "a person with a round face: keep a high or mid fade with a textured top styled upward/forward; avoid wide, flat styles that make the face look rounder."
      }
    ],
    square: [
      {
        title: "Textured Side-Swept Style",
        prompt: "a person with a square face: soften the jawline with a textured, slightly longer top and a gentle side-swept style; avoid very harsh boxy lines."
      },
      {
        title: "Modern Taper with Movement",
        prompt: "a person with a square face: go for a modern taper with messy texture and movement; keep edges clean but not overly sharp or flat on top."
      }
    ],
    heart: [
      {
        title: "Medium Length with Soft Fringe",
        prompt: "a person with a heart-shaped face: balance a wider forehead with medium length and soft fringe/side-sweep; avoid extreme height on top."
      },
      {
        title: "Textured with Fuller Sides",
        prompt: "a person with a heart-shaped face: keep the sides not too tight and add texture around the temples; aim for a fuller look near the jawline."
      }
    ],
    diamond: [
      {
        title: "Textured Fringe + Medium Length",
        prompt: "a person with a diamond face: add softness and width at the forehead with a textured fringe and medium length; avoid ultra-tight fades that over-emphasize cheekbones."
      },
      {
        title: "Side Part with Balanced Volume",
        prompt: "a person with a diamond face: choose a side part with a bit of length on the sides and controlled volume on top for balanced proportions."
      }
    ],
    oblong: [
      {
        title: "Textured Fringe + Moderate Height",
        prompt: "a person with an oblong face: reduce perceived length with a textured fringe and moderate top height; avoid tall pompadours or extra crown volume."
      },
      {
        title: "Fuller Sides + Forward Styling",
        prompt: "a person with an oblong face: keep the sides a little fuller (not a skin fade) and style the top forward with texture to shorten the face visually."
      }
    ]
  }
};

/**
 * Face shape mapping for Gemini responses
 * Maps non-standard face shape names to our canonical set
 */
export const FACE_SHAPE_MAPPING = {
  'rectangle': 'oblong',
  'long': 'oblong',
  'triangular': 'heart',
  'inverted triangle': 'heart',
  'inverted-triangle': 'heart'
};

/**
 * Canonical face shapes supported by the system
 */
export const CANONICAL_FACE_SHAPES = ['oval', 'round', 'square', 'heart', 'diamond', 'oblong'];

/**
 * Get suggestions for a specific face shape and gender
 * @param {string} faceShape - Detected face shape (will be normalized)
 * @param {string} gender - User's selected gender (men/women/other)
 * @returns {Array<{title: string, prompt: string}>} Array of 2 suggestion objects with title and prompt
 */
export function getFaceShapeSuggestions(faceShape, gender = 'men') {
  // Normalize face shape
  const normalizedShape = normalizeFaceShape(faceShape);
  
  // Normalize gender (default to 'men' if invalid)
  const normalizedGender = ['men', 'women', 'other'].includes(gender) ? gender : 'men';
  
  // Get suggestions
  const suggestions = FACE_SHAPE_SUGGESTIONS[normalizedGender]?.[normalizedShape];
  
  // Fallback to oval if shape not found
  if (!suggestions) {
    return FACE_SHAPE_SUGGESTIONS[normalizedGender].oval;
  }
  
  return suggestions;
}

/**
 * Normalize face shape to canonical form
 * @param {string} faceShape - Raw face shape from Gemini
 * @returns {string} Normalized face shape
 */
export function normalizeFaceShape(faceShape) {
  if (!faceShape || typeof faceShape !== 'string') {
    return 'oval'; // Default fallback
  }
  
  const lower = faceShape.toLowerCase().trim();
  
  // Check if it's already canonical
  if (CANONICAL_FACE_SHAPES.includes(lower)) {
    return lower;
  }
  
  // Apply mapping
  if (FACE_SHAPE_MAPPING[lower]) {
    return FACE_SHAPE_MAPPING[lower];
  }
  
  // Fallback to oval
  return 'oval';
}

